export enum SearchEngines {
  Google = 1,
  Facebook = 5,
  Snapchat = 30,
  GoogleMerchantCenter = 73
}

export enum CreativeSelections {
  Background = 1,
  Overlay = 2
}

// contentSections.ts
export enum ContentSections {
  Title = 1,
  Description = 2,
  LabelsKeys = 3,
  KeySpecificationFeatures = 4,
  Custom = 5
}

export enum CatalogCreationStep {
  ExtractAndFill = 1,
  Standarization = 2,
  Enhancement = 3,
  Summary = 4
}

export enum CatalogSetupIndex {
  Details = 0,
  Rulebuilders = 1,
  Enrichment = 2
}

export enum ContentEnrichmentIndex {
  Title = 0,
  Description = 1
}

export enum DescriptionLength {
  None = 0,
  Brief = 1,
  Balanced = 2,
  Detailed = 3
}

export enum Tone {
  None = 0,
  Descriptive = 1,
  Luxury = 2,
  Trendy = 3,
  Casual = 4,
  Professional = 5,
  Performance = 6,
  Minimalist = 7,
  Vibrant = 8,
  Season = 9,
  Festival = 10
}

export enum Macros {
  Brand = 1,
  ProductType = 2,
  Size = 3,
  Material = 4,
  Color = 5,
  Gender = 6,
  Title = 7,
  Keywords = 8
}

export enum CatalogStatus {
  None = 0,
  New = 1,
  Draft = 2,
  Processing = 3,
  Processed = 4,
  ProcessingFailed = 5,
  Deleted = 6,
  Paused = 8,
  SyncInitiated = 9
}

export enum CatalogType {
  None = 0,
  Source = 1,
  Destination = 2
}

export enum PreviewType {
  None = 0,
  Original = 1,
  Enriched = 2
}

export const SearchEngine_Icon = {
  [SearchEngines.Facebook]: {
    name: 'Facebook',
    icon: '/assets/static/images/metaicon.png'
  },
  [SearchEngines.Google]: {
    name: 'Google',
    icon: '/assets/static/images/googleicon.png'
  },
  [SearchEngines.Snapchat]: {
    name: 'Snapchat',
    icon: '/assets/static/images/snapchat.png'
  }
}

export const CatalogStatusConfig: Record<
  CatalogStatus,
  { label: string; icon: string; color: string; bg: string }
> = {
  [CatalogStatus.None]: {
    label: 'Unknown',
    icon: 'mdi-help-circle',
    color: '#757575',
    bg: '#f5f5f5'
  },
  [CatalogStatus.New]: {
    label: 'New',
    icon: 'mdi-plus-circle',
    color: '#1976d2',
    bg: '#e3f2fd'
  },
  [CatalogStatus.Draft]: {
    label: 'Draft',
    icon: 'mdi-file-outline',
    color: '#1976d2',
    bg: '#e3f2fd'
  },
  [CatalogStatus.Processing]: {
    label: 'Processing',
    icon: 'mdi-progress-clock',
    color: '#51b84b',
    bg: '#e8f5e9'
  },
  [CatalogStatus.Processed]: {
    label: 'Processed',
    icon: 'mdi-check-circle',
    color: '#51b84b',
    bg: '#e8f5e9'
  },
  [CatalogStatus.ProcessingFailed]: {
    label: 'Processed',
    icon: 'mdi-alert-circle',
    color: '#ff9800',
    bg: '#fff8e1'
  },
  [CatalogStatus.Paused]: {
    label: 'Paused',
    icon: 'mdi-pause-circle',
    color: '#9e9e9e',
    bg: '#f0f0f0'
  },
  [CatalogStatus.SyncInitiated]: {
    label: 'Sync Initiated',
    icon: '',
    color: '',
    bg: ''
  }
}

export const getChannelIconList = (channels: number[] | undefined | null) => [
  {
    src: getChannelIcons([SearchEngines.Google])[0],
    alt: 'Google',
    active: channels?.includes(SearchEngines.Google)
  },
  {
    src: getChannelIcons([SearchEngines.Facebook])[0],
    alt: 'Facebook',
    active: channels?.includes(SearchEngines.Facebook)
  },
  {
    src: getChannelIcons([SearchEngines.Snapchat])[0],
    alt: 'Snapchat',
    active: channels?.includes(SearchEngines.Snapchat)
  }
]

export const getChannelIcons = (
  channelIds: number[] | null | undefined
): string[] | null => {
  if (!channelIds || !Array.isArray(channelIds)) return null

  return channelIds.map(id => {
    const searchEngineKey = id as SearchEngines
    const channelIcon =
      SearchEngine_Icon[searchEngineKey]?.icon || 'mdi-help-circle'
    return channelIcon
  })
}

export interface LinkedCatalogResponse {
  engineCatalogId: string
  linkedCatalogIds: number[]
}

// ============================================================================
// CHANNEL FEATURE CONFIGURATION
// ============================================================================

export enum ChannelFeature {
  Sync = 'sync',
  UploadSummary = 'uploadSummary',
  CreativeEnrichmentSummary = 'creativeEnrichmentSummary'
}

export type ChannelFeatureValue = boolean | Partial<Record<string, boolean>>

export type ChannelFeatureFlags = Partial<
  Record<ChannelFeature, ChannelFeatureValue>
>

export enum UploadSummaryField {
  ItemsUpdated = 'itemsUpdated',
  InvalidItems = 'InvalidItems',
  ItemsRemoved = 'itemsRemoved'
}

export const CHANNEL_FEATURE_CONFIG: Record<
  SearchEngines,
  ChannelFeatureFlags
> = {
  [SearchEngines.Google]: {
    [ChannelFeature.Sync]: true,
    [ChannelFeature.UploadSummary]: {
      [UploadSummaryField.ItemsUpdated]: true,
      [UploadSummaryField.InvalidItems]: true,
      [UploadSummaryField.ItemsRemoved]: false
    },
    [ChannelFeature.CreativeEnrichmentSummary]: false
  },
  [SearchEngines.Facebook]: {
    [ChannelFeature.Sync]: true,
    [ChannelFeature.UploadSummary]: true,
    [ChannelFeature.CreativeEnrichmentSummary]: true
  },
  [SearchEngines.Snapchat]: {
    [ChannelFeature.Sync]: true,
    [ChannelFeature.UploadSummary]: true,
    [ChannelFeature.CreativeEnrichmentSummary]: true
  }
}

export const isFeatureEnabled = (
  channel: SearchEngines | null | undefined,
  feature: ChannelFeature
): boolean => {
  if (channel === null || channel === undefined) return false
  const value = CHANNEL_FEATURE_CONFIG[channel]?.[feature]
  if (typeof value === 'boolean') return value
  return typeof value === 'object' && value !== null
}

export const getVisibleFields = (
  channels: SearchEngines[] | number[] | null | undefined,
  feature: ChannelFeature
): Record<string, boolean> => {
  if (!Array.isArray(channels) || channels.length === 0) return {}

  const merged: Record<string, boolean> = {}

  for (const channel of channels) {
    const value = CHANNEL_FEATURE_CONFIG[channel as SearchEngines]?.[feature]
    if (typeof value === 'object' && value !== null) {
      for (const [field, visible] of Object.entries(value)) {
        if (visible) merged[field] = true
        else if (!(field in merged)) merged[field] = false
      }
    }
  }

  return merged
}

export const hasAnyChannelWithFeature = (
  channels: SearchEngines[] | number[] | null | undefined,
  feature: ChannelFeature
): boolean => {
  if (!Array.isArray(channels) || channels.length === 0) return false
  return channels.some(channel =>
    isFeatureEnabled(channel as SearchEngines, feature)
  )
}

export const SUPPORTED_SYNC_ENGINES: SearchEngines[] = Object.entries(
  CHANNEL_FEATURE_CONFIG
)
  .filter(([, flags]) => flags[ChannelFeature.Sync] === true)
  .map(([id]) => Number(id) as SearchEngines)

export const isEngineSyncSupported = (
  channel: SearchEngines | null | undefined
): boolean => isFeatureEnabled(channel, ChannelFeature.Sync)

export const hasAnySupportedSyncEngine = (
  channels: SearchEngines[] | null | undefined
): boolean => hasAnyChannelWithFeature(channels, ChannelFeature.Sync)

export const formatCurrencyPrice = (
  amount: number,
  currencyCode?: string
): string =>
  new Intl.NumberFormat(
    undefined,
    currencyCode
      ? { style: 'currency', currency: currencyCode }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  ).format(amount)
